'use client';

import { useState, useEffect, useCallback } from 'react';
import { LocaleDetails } from 'shared/src/types/languages.type';
import { BlogItem } from './blog-item';
import { Article } from 'shared/src/types/article.type';
import { articleAdminUIContent } from '@/lib/ui-content/article-ui-content';

interface ArticleListProps {
  initialArticles: Article[];
  locale: LocaleDetails;
  clientId: string;
  lang: string;
  isAdmin: boolean;
  hasMore: boolean;
}

export const ArticleList = ({ initialArticles, locale, clientId, lang, isAdmin, hasMore: initialHasMore }: ArticleListProps) => {
  const uiContent = articleAdminUIContent[locale.languageCode];
  const [articles, setArticles] = useState<Article[]>(initialArticles);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [showLoadMore, setShowLoadMore] = useState(false);
  const [showFooterShortcut, setShowFooterShortcut] = useState(false);
  const [lastDocId, setLastDocId] = useState<string | null>(
    initialArticles.length > 0 ? initialArticles[initialArticles.length - 1].id : null,
  );

  const INITIAL_AUTO_LOAD_LIMIT = 12; // Only auto-load first 12 articles

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    try {
      const response = await fetch('/api/articles/load-more', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lang,
          clientId,
          isAdmin,
          lastDocId,
        }),
      });

      const data = await response.json();

      if (data.articles && data.articles.length > 0) {
        setArticles((prev) => [...prev, ...data.articles]);
        setLastDocId(data.articles[data.articles.length - 1].id);
        setHasMore(data.hasMore);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more articles:', error);
    } finally {
      setLoading(false);
    }
  }, [loading, hasMore, lang, clientId, isAdmin, lastDocId]);

  const scrollToFooter = () => {
    // Scroll to the bottom of the page smoothly
    window.scrollTo({
      top: document.documentElement.scrollHeight,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      // Show footer shortcut when user has scrolled past a few articles
      const scrollPosition = window.scrollY;
      setShowFooterShortcut(scrollPosition > 800);

      // After INITIAL_AUTO_LOAD_LIMIT articles, don't auto-load anymore
      if (articles.length >= INITIAL_AUTO_LOAD_LIMIT) {
        setShowLoadMore(true);
        return;
      }

      if (
        window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 200 &&
        !loading &&
        hasMore
      ) {
        loadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [articles.length, loadMore, loading, hasMore]);

  if (articles.length === 0) {
    return null;
  }

  return (
    <>
      <div className="flex items-start gap-8 max-lg:flex-col">
        {/* Featured Article - Sticky on Desktop */}
        <a
          className="block w-full lg:sticky lg:top-20 lg:w-[45%] group"
          href={`/${locale.path}/articles/${articles[0].slug || articles[0].id}${clientId ? `?clientId=${clientId}` : ''}`}
        >
          <BlogItem locale={locale} article={articles[0]} main />
        </a>

        {/* Regular Articles List */}
        <div className="flex w-full flex-col gap-6 lg:w-[55%]">
          {articles.slice(1).map((article) => (
            <a
              className="block group no-underline"
              href={`/${locale.path}/articles/${article.slug || article.id}${clientId ? `?clientId=${clientId}` : ''}`}
              key={article.id}
            >
              <BlogItem locale={locale} article={article} />
            </a>
          ))}

          {/* Loading indicator */}
          {loading && (
            <div className="flex justify-center py-8">
              <div className="flex items-center gap-3 text-gray-600 dark:text-gray-400">
                <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-gray-600 dark:border-t-blue-400"></div>
                <span className="text-sm font-medium">{uiContent.loadingArticles}</span>
              </div>
            </div>
          )}

          {/* Load More Button - appears after initial auto-load limit */}
          {hasMore && showLoadMore && !loading && (
            <div className="flex flex-col items-center gap-4 py-8">
              <button
                onClick={loadMore}
                className="group flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-medium text-gray-700 shadow-sm transition-all hover:shadow-md hover:scale-105 dark:bg-gray-800 dark:text-gray-200 border border-gray-200 dark:border-gray-700"
              >
                <span>{uiContent.loadMoreArticles}</span>
                <svg 
                  className="h-4 w-4 transition-transform group-hover:translate-y-0.5" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {/* Footer shortcut inline */}
              <button
                onClick={scrollToFooter}
                className="flex items-center gap-2 text-xs text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
              >
                <span>{uiContent.orSkipToFooter}</span>
                <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </button>
            </div>
          )}

          {/* End of articles message */}
          {!hasMore && articles.length > 0 && (
            <div className="py-8 text-center">
              <div className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{uiContent.reachedTheEnd}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Floating Footer Shortcut - appears when scrolled */}
      {showFooterShortcut && hasMore && (
        <button
          onClick={scrollToFooter}
          className="fixed bottom-8 right-8 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-lg transition-all hover:scale-110 hover:shadow-xl dark:bg-gray-800 border border-gray-200 dark:border-gray-700 group"
          title={uiContent.skipToFooter}
        >
          <svg 
            className="h-5 w-5 text-gray-600 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" 
            fill="none" 
            viewBox="0 0 24 24" 
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </button>
      )}
    </>
  );
};
